import React from 'react';
import { connect } from 'react-redux';
import { Box, IconButton, Grid, InputBase, Tooltip, Paper, Typography } from '@mui/material';
import { withStyles } from 'tss-react/mui';
import ClearRoundedIcon from '@mui/icons-material/ClearRounded';
import { createActions } from '../../store';
import CreatePageConstants from '../../CreatePageConstants';
import { detectLyricsSegmentAddPress, detectLyricsSegmentDeletePress, detectEscapePress, detectLyricsSegmentMoveUpPress, detectLyricsSegmentMoveDownPress, detectLyricsSegmentExpandPress, detectLyricsSegmentShortenPress } from '../../../../../utilities/eventUtilities';
import clsx from 'clsx';
import { convertToLyricsTime } from '../../../../../utilities/lyricsUtilities';
import { previewHistoryBatch } from '../../store/extensions';
import SegmentCheckbox from './SegmentCheckbox';

const draggableLineSize = 6;
const segmentContentContainerId = 'segment-content-container';
const segmentStaticTextId = 'segment-static-text';
const segmentCursorDefault = 'grab';
const segmentCursorActive = 'grabbing';
const draggableLineCursorDefault = 'row-resize';
const draggableLineCursorActive = 'row-resize';

const styles = theme => ({
    timelineItemBox: {
        width: '80%',
        position: 'absolute',
        right: 16,
        overflow: 'hidden',
        borderLeft: 'none',
        borderRadius: '0 6px 6px 0',
        boxShadow: 'none',
        '& [unselected-checkbox=true]': {
            display: 'flex'
        },
        '&:hover [unselected-checkbox]': {
            display: 'flex'
        }
    },
    timelineItemBoxDefaultBackground: {
        background: theme.palette.background.paperLight,
    },
    timelineItemBoxCheckedBackground: {
        background: theme.palette.secondary.withAlpha,
    },
    timelineItemBoxActive: {
        // borderColor: theme.overrides.MuiPaper.rounded.borderActive
    },
    timelineItemBoxTouched: {
        opacity: 0.5
    },
    deleteIcon: {
        color: theme.palette.text.secondary,
        position: 'absolute',
        top: 0,
        right: 0
    },
    draggableLine: {
        height: draggableLineSize,
        opacity: 0.4
    },
    segmentStaticText: {
        fontSize: '10pt',
        marginLeft: 8,
        textAlign: 'left',
        lineHeight: 1.2,
        letterSpacing: 0.1,
        fontWeight: 400,
        whiteSpace: 'pre-wrap',
        '&:focus': {
            outline: 'none'
        }
    }
});

const SegmentTextField = withStyles(InputBase, {
    root: {
        fontSize: '10pt',
        marginLeft: 8
    },
    input: {
        fontWeight: 400,
        lineHeight: 1.2,
        letterSpacing: 0.1
    },
    multiline: {
        padding: 0
    }
});

const RemoveSegmentIcon = withStyles(ClearRoundedIcon, {
    root: { 
        width: '0.7em',
        height: '0.7em'
    }
});

class LyricsSegment extends React.Component {

    state = {
        topDraggableLineTouched: false,
        bottomDraggableLineTouched: false,
        segmentPanelTouched: false,
        segmentTouchedTopHalf: 0,
        segmentTouchedBottomHalf: 0,
        segmentPanelCursor: null,
        textFieldFocused: false,
        cursorTouchedPositionY: 0,
        cursorOutOfSafeRange: false,
        contentMaxHeight: ''
    };
    lyricsContainerElement = null;

    render() {
        const { classes, lyricsSegment, index } = this.props;
        const { segmentPanelTouched, segmentPanelCursor, contentMaxHeight, textFieldFocused } = this.state;

        const scaleFactor = CreatePageConstants.LYRICS_TIMELINE.SCALE_FACTOR;
        const segmentHeight = (lyricsSegment.endTime - lyricsSegment.startTime) * scaleFactor;

        const segmentBoxClasses = clsx({
            [classes.timelineItemBox]: true,
            [classes.timelineItemBoxDefaultBackground]: !lyricsSegment.checked,
            [classes.timelineItemBoxCheckedBackground]: lyricsSegment.checked,
            [classes.timelineItemBoxActive]: textFieldFocused,
            [classes.timelineItemBoxTouched]: segmentPanelTouched
        });

        return (
            <Paper
                className={segmentBoxClasses} 
                style={{
                    top: lyricsSegment.startTime * scaleFactor, 
                    height: segmentHeight
                }}
                onMouseEnter={this.onSegmentAreaEnter}
                endtime={lyricsSegment.endTime}
            >
                <Box 
                    className={classes.draggableLine}
                    onMouseDown={this.onTopDraggableLineMouseDown}
                    onMouseEnter={() => this.setBodyCursor(draggableLineCursorDefault)}
                    onMouseLeave={() => this.setBodyCursor('auto')}
                >
                </Box>
                <Grid
                    id={segmentContentContainerId}
                    container
                    direction='row'
                    justifyContent='space-between'
                    alignItems='center'
                    style={{ 
                        height: segmentHeight - (draggableLineSize * 2) - 2,
                        cursor: segmentPanelCursor 
                    }}
                    onMouseDown={this.onSegmentPanelMouseDown}
                    onMouseUp={this.onSegmentPanelMouseUp}
                    onMouseEnter={() => this.setBodyCursor(segmentCursorDefault)}
                    onMouseLeave={() => this.setBodyCursor('auto')}
                >
                    <Grid size={{ xs: 9 }}>
                        {textFieldFocused
                        ?
                            <SegmentTextField
                                inputRef={this.focusTextInput}
                                value={lyricsSegment.text}
                                placeholder='Lyric text'
                                fullWidth
                                multiline
                                inputProps={{ style: { maxHeight: contentMaxHeight } }}  
                                onChange={this.onSegmentTextChange}
                                onFocus={this.onSegmentTextFocusIn}
                                onBlur={this.onSegmentTextFocusOut}
                                onKeyDown={this.onSegmentTextKeyDown}
                            />
                        :
                            <Typography 
                                id={segmentStaticTextId}
                                variant='body1' 
                                className={classes.segmentStaticText}
                                tabIndex='0'
                                onFocus={this.onSegmentLabelFocusIn}
                            >
                                {lyricsSegment.text}
                            </Typography>
                        }
                    </Grid>
                    <Grid size={{ xs: 1 }}>
                        <Tooltip 
                            title='Remove' 
                            placement='right'
                        >
                            <IconButton
                                className={classes.deleteIcon}
                                size='small'
                                color='inherit'
                                onClick={this.onSegmentRemove}
                                onMouseDown={this.onDeleteButtonMouseDown}
                                aria-label='Remove Lyrics'
                                disableFocusRipple={true}
                                onFocus={this.onDeleteButtonFocusIn}
                                tabIndex={1}
                            >
                                <RemoveSegmentIcon />
                            </IconButton>
                        </Tooltip>
                        
                        {/* Render the checkbox for lyrics segment */}
                        <SegmentCheckbox 
                            index={index}
                            segmentChecked={lyricsSegment.checked}
                        />
                    </Grid>
                </Grid>
                <Box 
                    className={classes.draggableLine}
                    onMouseDown={this.onBottomDraggableLineMouseDown}
                    onMouseEnter={() => this.setBodyCursor(draggableLineCursorDefault)}
                    onMouseLeave={() => this.setBodyCursor('auto')}
                ></Box>
            </Paper>
        );
    }

    componentDidMount() {
        const { lyricsSegment } = this.props;

        // We have a feature to focus a text input when it's creating but it causes the jumping effect when we scroll the panel.
        // So that we focus the newly created elements (created less than 1 second ago) and avoid old ones.
        // If the initializationTime is missing the behavior will be as for newly created one.
        const isNewlyCreatedSegment = lyricsSegment.initializationTime 
            ? (((new Date()).getTime() - lyricsSegment.initializationTime.getTime()) / 1000) < 1 
            : true;
        
        if (!lyricsSegment.text && isNewlyCreatedSegment) {
            this.setState({textFieldFocused: true});
        }

        this.lyricsContainerElement = document.getElementById('lyrics-timeline-container');
    }

    componentWillUnmount() {
        this.lyricsContainerElement = undefined;
    }

    focusTextInput = (inputElement) => {
        // Focus manually instead of using autoFocus: the native autofocus scrolls the panel
        // to the vertically centered text field, which jumps to the middle of long segments
        if (inputElement && document.activeElement !== inputElement) {
            inputElement.focus({ preventScroll: true });
        }
    }

    onSegmentAreaEnter = () => {
        const { hideLyricsActionsButton } = this.props;
        hideLyricsActionsButton();
    }

    removeInvalidSymbols = (text) => {
      return text.replace(/\p{Emoji}(?:\u200D\p{Emoji})+/gu, '');
    }

    onSegmentTextChange = (event) => {
        const value = this.removeInvalidSymbols(event.target.value);
        
        if (value.length < CreatePageConstants.LYRICS_TIMELINE.SEGMENT_TEXT_MAX_LENGTH) {
            this.updateSegmentText(value);
        }
    }

    onSegmentTextFocusIn = (event) => {
        this.setContentMaxHeight();

        // Set the cursor to end of text when an input gets the focus
        event.target.selectionStart = event.target.value.length;
        event.target.selectionEnd = event.target.value.length;
    }

    onSegmentTextFocusOut = () => {
        this.setState({textFieldFocused: false});
        previewHistoryBatch.end();
    }

    onDeleteButtonFocusIn = () => {
        if (!this.state.segmentPanelTouched) {
            this.setState({textFieldFocused: true});
        }
    }

    onSegmentLabelFocusIn = () => {
        if (!this.state.segmentPanelTouched) {
            this.setState({textFieldFocused: true});
        }
    }

    onSegmentTextKeyDown = (event) => {
        previewHistoryBatch.start();
        
        // Disable this keys combination because we handle it on the segments timeline 
        detectLyricsSegmentAddPress(event, () => {
            event.preventDefault();
        });

        // Detect escape press to switch on top level and move the preview cursor
        detectEscapePress(event, () => {
            window.focus();
            
            if (document.activeElement)
                document.activeElement.blur();

            event.preventDefault();
        });

        // Detect a key to delete the segment
        detectLyricsSegmentDeletePress(event, this.onSegmentRemove);

        let resizeSegmentDetected = false;

        // Detect a key to increase time of segment
        detectLyricsSegmentExpandPress(event, () => {
            this.expandSegmentDuration(CreatePageConstants.LYRICS_TIMELINE.HOTKEYS_SEGMENT_RESIZE_STEP);
            event.preventDefault();
            resizeSegmentDetected = true;
        });

        // Detect a key to decrease time of segment
        detectLyricsSegmentShortenPress(event, () => {
            this.shortenSegmentDuration(CreatePageConstants.LYRICS_TIMELINE.HOTKEYS_SEGMENT_RESIZE_STEP);
            event.preventDefault();
            resizeSegmentDetected = true;
        });

        if (!resizeSegmentDetected) {
            // Detect a key to move segment up
            detectLyricsSegmentMoveUpPress(event, () => {
                this.moveSegmentPositionUp(CreatePageConstants.LYRICS_TIMELINE.HOTKEYS_SEGMENT_MOVE_STEP);
                event.preventDefault();
            });

            // Detect a key to move segment down
            detectLyricsSegmentMoveDownPress(event, () => {
                this.moveSegmentPositionDown(CreatePageConstants.LYRICS_TIMELINE.HOTKEYS_SEGMENT_MOVE_STEP);
                event.preventDefault();
            });
        }
    }

    onDeleteButtonMouseDown = (event) => {
        // Keep the focus where it is: focusing the button switches the segment to edit mode
        // and the autofocused text field scrolls the timeline to the segment center
        event.preventDefault();
        event.stopPropagation();
    }

    onSegmentRemove = (event) => {
        const { lyricsSegment, removeLyricsSegment } = this.props;

        if (event) {
            event.preventDefault();
            event.stopPropagation();
        }

        removeLyricsSegment(lyricsSegment);

        window._bodyCursorBlocked = false;
        this.setBodyCursor('auto');
    }

    onTopDraggableLineMouseDown = () => {
        const { setProperty, hideLyricsActionsButton } = this.props;
        
        this.setBodyCursor(draggableLineCursorActive);
        window._bodyCursorBlocked = true;

        this.setState({
            topDraggableLineTouched: true,
            segmentPanelCursor: null
        });

        this.assignDragAndDropEvents();
        hideLyricsActionsButton();
        setProperty('lyricsSegmentDragging', true);
    }

    onSegmentPanelMouseDown = (event) => {
        const { segmentPanelTouched } = this.state;
        const isNecessaryElement = event.target.id && (event.target.id === segmentContentContainerId || event.target.id === segmentStaticTextId);
        
        if (isNecessaryElement && !segmentPanelTouched) {
            const { setProperty, hideLyricsActionsButton } = this.props;

            const containerRect = event.currentTarget.parentElement.getBoundingClientRect();
            const segmentRect = event.currentTarget.getBoundingClientRect();

            const cursorPosition = (event.pageY - containerRect.top);
            const segmentStartPosition = segmentRect.top - containerRect.top;
            
            this.setBodyCursor(segmentCursorActive);
            window._bodyCursorBlocked = true;

            this.setState({
                segmentPanelTouched: true,
                segmentTouchedTopHalf: cursorPosition - segmentStartPosition,
                segmentTouchedBottomHalf: (segmentStartPosition + containerRect.height) - cursorPosition,
                segmentPanelCursor: segmentCursorActive,
                cursorTouchedPositionY: event.pageY
            });

            this.assignDragAndDropEvents();
            hideLyricsActionsButton();
            setProperty('lyricsSegmentDragging', true);
        }
    }

    onSegmentPanelMouseUp = (event) => {
        const { cursorTouchedPositionY } = this.state;
        
        if (cursorTouchedPositionY === event.pageY) {
            this.setState({
                cursorTouchedPositionY: 0,
                textFieldFocused: true
            });
        }
    }

    onBottomDraggableLineMouseDown = () => {
        const { setProperty, hideLyricsActionsButton } = this.props;

        this.setBodyCursor(draggableLineCursorActive);
        window._bodyCursorBlocked = true;

        this.setState({
            bottomDraggableLineTouched: true,
            segmentPanelCursor: null
        });
        this.assignDragAndDropEvents();
        
        hideLyricsActionsButton();
        setProperty('lyricsSegmentDragging', true);
    }

    onMouseMove = (event) => {
        if ((this.state.topDraggableLineTouched || this.state.bottomDraggableLineTouched || this.state.segmentPanelTouched) && this.lyricsContainerElement) {
            previewHistoryBatch.start();

            // The main function that changes the duration/position of a segment 
            const containerRect = this.lyricsContainerElement.getBoundingClientRect();
            const cursorPosition = (event.pageY - containerRect.top);

            this.transformCursorToSegmentPosition(cursorPosition, event.pageY);
        }
    }

    setContentMaxHeight = () => {
        const { lyricsSegment } = this.props;
        const scaleFactor = CreatePageConstants.LYRICS_TIMELINE.SCALE_FACTOR;
        const segmentHeight = (lyricsSegment.endTime - lyricsSegment.startTime) * scaleFactor;
        this.setState({ contentMaxHeight: `${segmentHeight - 10}px` });
    }

    onStopDragging = (event) => {
        const { setProperty } = this.props;
        previewHistoryBatch.end();

        this.setState({
            topDraggableLineTouched: false, 
            bottomDraggableLineTouched: false,
            segmentPanelTouched: false,
            segmentTouchedTopHalf: 0,
            segmentTouchedBottomHalf: 0,
            segmentPanelCursor: segmentCursorDefault,
            cursorTouchedPositionY: 0,
            cursorOutOfSafeRange: false
        });

        this.removeDragAndDropEvents();
        window._bodyCursorBlocked = false;
        this.setBodyCursor('auto');
        setProperty('lyricsSegmentDragging', false);
    }

    assignDragAndDropEvents = () => {
        window.addEventListener('mousemove', this.onMouseMove);
        window.addEventListener('mouseup', this.onStopDragging);
        window.addEventListener('mouseleave', this.onStopDragging);

        document.onselectstart = () => { return false; };
        document.onselectionchange = () => { return false; };
    }

    removeDragAndDropEvents = () => {
        window.removeEventListener('mousemove', this.onMouseMove);
        window.removeEventListener('mouseup', this.onStopDragging);
        window.removeEventListener('mouseleave', this.onStopDragging);

        document.onselectstart = null;
        document.onselectionchange = null;
    }

    setBodyCursor = (cursor) => {
        if (!window._bodyCursorBlocked)
            document.body.style.cursor = cursor;
    }

    updateSegmentText = (value) => {
        const { updateLyricsSegment, index } = this.props;
        updateLyricsSegment(index, { text: value });
    }

    moveSegmentPositionUp = (duration) => {
        const { setLyricsSegmentTime, lyricsSegment, hideLyricsActionsButton, audioDuration } = this.props;

        let startTime = convertToLyricsTime(lyricsSegment.startTime - duration);
        let endTime = convertToLyricsTime(lyricsSegment.endTime - duration);
        const freezedDuration = convertToLyricsTime(endTime - startTime);

        hideLyricsActionsButton();
        setLyricsSegmentTime(lyricsSegment, startTime, endTime, freezedDuration, audioDuration);
    }

    moveSegmentPositionDown = (duration) => {
        const { setLyricsSegmentTime, lyricsSegment, hideLyricsActionsButton, audioDuration } = this.props;

        let startTime = convertToLyricsTime(lyricsSegment.startTime + duration);
        let endTime = convertToLyricsTime(lyricsSegment.endTime + duration);
        const freezedDuration = convertToLyricsTime(endTime - startTime);

        hideLyricsActionsButton();
        setLyricsSegmentTime(lyricsSegment, startTime, endTime, freezedDuration, audioDuration);
    }

    expandSegmentDuration = (duration) => {
        const { setLyricsSegmentTime, lyricsSegment, hideLyricsActionsButton, audioDuration } = this.props;
        let endTime = convertToLyricsTime(lyricsSegment.endTime + duration);

        hideLyricsActionsButton();
        setLyricsSegmentTime(lyricsSegment, lyricsSegment.startTime, endTime, null, audioDuration);
    }

    shortenSegmentDuration = (duration) => {
        const { setLyricsSegmentTime, lyricsSegment, hideLyricsActionsButton, audioDuration } = this.props;
        let endTime = convertToLyricsTime(lyricsSegment.endTime - duration);
        let changedDuration = endTime - lyricsSegment.startTime;

        if (changedDuration < CreatePageConstants.LYRICS_TIMELINE.DEFAULT_SEGMENT_DURATION)
            endTime = lyricsSegment.startTime + CreatePageConstants.LYRICS_TIMELINE.DEFAULT_SEGMENT_DURATION;
        
        hideLyricsActionsButton();
        setLyricsSegmentTime(lyricsSegment, lyricsSegment.startTime, endTime, null, audioDuration);
    }

    transformCursorToSegmentPosition = (cursorPosition, pageY) => {
        const scaleFactor = CreatePageConstants.LYRICS_TIMELINE.SCALE_FACTOR;
        const { setLyricsSegmentTime, lyricsSegment, audioDuration } = this.props;

        const { 
            segmentTouchedTopHalf, segmentTouchedBottomHalf, topDraggableLineTouched, bottomDraggableLineTouched, 
            segmentPanelTouched, cursorTouchedPositionY, cursorOutOfSafeRange 
        } = this.state;

        if (segmentPanelTouched) {
            // When we move the whole panel

            const safeTopBorder = cursorTouchedPositionY - CreatePageConstants.LYRICS_TIMELINE.DRAGGING_CURSOR_TOP_SAFE_SHIFT;
            const safeBottomBorder = cursorTouchedPositionY + CreatePageConstants.LYRICS_TIMELINE.DRAGGING_CURSOR_BOTTOM_SAFE_SHIFT;

            // Detect the safe range to avoid immediately movement
            if (!(pageY > safeTopBorder && pageY < safeBottomBorder) || cursorOutOfSafeRange) {
                let startTime = convertToLyricsTime((cursorPosition - segmentTouchedTopHalf) / scaleFactor);
                let endTime = convertToLyricsTime((cursorPosition + segmentTouchedBottomHalf) / scaleFactor);
                const segmentDuration = convertToLyricsTime(endTime - startTime);

                if (!cursorOutOfSafeRange)
                    this.setState({ cursorOutOfSafeRange: true });

                setLyricsSegmentTime(lyricsSegment, startTime, endTime, segmentDuration, audioDuration);
            }
        } else {
            // When we resize the top or bottom side
            const cursorTime = convertToLyricsTime(cursorPosition / scaleFactor);
            const resizedStartTime = topDraggableLineTouched ? cursorTime : lyricsSegment.startTime;
            const resizedEndTime = bottomDraggableLineTouched ? cursorTime : lyricsSegment.endTime;
            const resizedDuration = convertToLyricsTime(resizedEndTime - resizedStartTime);

            if (resizedDuration >= CreatePageConstants.LYRICS_TIMELINE.DEFAULT_SEGMENT_DURATION)
                setLyricsSegmentTime(lyricsSegment, resizedStartTime, resizedEndTime, null, audioDuration);
        }
    }
}

const mapStateToProps = (state, ownProps) => {
    // Bind the component to an item from list in order to render it separately
    const lyricsSegment = state.createPreview.present.videoSettings.lyrics.segments[ownProps.index];

    const { 
        audioDuration
    } = state.create; 

    return {
        lyricsSegment,
        audioDuration
    };
}

const mapDispatchToProps = (dispatch) => {
    const setProperty = (key, value) => dispatch(createActions.setProperty(key, value));
    const updateLyricsSegment = (index, properties) => dispatch(createActions.updateLyricsSegment(index, properties));
    const removeLyricsSegment = (lyricsSegment) => dispatch(createActions.removeLyricsSegment(lyricsSegment));
    const setLyricsSegmentTime = (segment, startTime, endTime, freezedDuration, audioDuration) => dispatch(createActions.setLyricsSegmentTime(segment, startTime, endTime, freezedDuration, audioDuration)); 
    const hideLyricsActionsButton = () => dispatch(createActions.setProperty('lyricsActionsPanelTime', -1));

    return {
        setProperty,
        updateLyricsSegment,
        removeLyricsSegment,
        setLyricsSegmentTime,
        hideLyricsActionsButton
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(withStyles(LyricsSegment, styles));